// ═══════════════════════════════════════════════════════════════
// HotWash — Full AI Analysis (Claude-powered)
// Matches original Firebase app: executive summary, strengths,
// critical findings, themes, prioritized recommendations
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import {
  Paper, Text, Button, Group, Stack, Badge, SimpleGrid,
  Loader, Center, ThemeIcon,
} from '../gab/GabComponents';
import {
  IconSparkles, IconCheck, IconAlertTriangle, IconAlertCircle,
  IconCopy, IconBulb,
} from '@tabler/icons-react';
import type { Observation, Incident } from '../../types';
import { callClaude } from '../../lib/api-switch';
import { secondary, semantic } from '../../lib/colors';

// ── Types matching original backend response ────────────────
interface CriticalFinding {
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
}

interface Theme {
  theme: string;
  description: string;
  observationCount: number;
  recommendations: string[];
}

interface PrioritizedRec {
  priority: number;
  action: string;
  rationale: string;
  timeframe: 'immediate' | 'short-term' | 'long-term';
}

interface OverallAssessment {
  score: number;
  label: string;
  summary: string;
}

interface AIResult {
  executiveSummary: string;
  keyStrengths: string[];
  criticalFindings: CriticalFinding[];
  themes: Theme[];
  prioritizedRecommendations: PrioritizedRec[];
  riskAreas: string[];
  overallAssessment: OverallAssessment;
  source: 'claude' | 'heuristic';
}

interface Props {
  incident: Incident;
  observations: Observation[];
}

// ── Heuristic fallback (when Claude unavailable) ────────────
function generateHeuristicAnalysis(incident: Incident, obs: Observation[]): AIResult {
  const total = obs.length;
  const critical = obs.filter(o => o.tier === 'critical_error');
  const problems = obs.filter(o => o.tier === 'problem_identified');
  const improvements = obs.filter(o => o.tier === 'improvement_needed');
  const withRemedy = obs.filter(o => o.suggestedRemedy).length;
  const remedyPct = total > 0 ? Math.round((withRemedy / total) * 100) : 0;

  let score = 10;
  score -= (critical.length / Math.max(total, 1)) * 5;
  score -= (problems.length / Math.max(total, 1)) * 2.5;
  score += (withRemedy / Math.max(total, 1)) * 1.5;
  score = Math.max(1, Math.min(10, Math.round(score * 10) / 10));

  const label = score >= 8 ? 'Strong' : score >= 6 ? 'Adequate' : score >= 4 ? 'Needs Improvement' : 'Critical';

  const keyStrengths: string[] = [];
  if (remedyPct >= 70) keyStrengths.push('Strong remedy documentation across observations');
  if (critical.length === 0) keyStrengths.push('No critical errors identified');
  const obsOnly = obs.filter(o => o.tier === 'observation');
  if (obsOnly.length > total * 0.5) keyStrengths.push('Majority of findings are neutral observations');
  if (new Set(obs.map(o => o.submittedBy)).size > 3) keyStrengths.push('Diverse contributor participation');
  if (keyStrengths.length === 0) keyStrengths.push('Data collection is in progress');

  const riskAreas: string[] = [];
  if (critical.length > 0) riskAreas.push(`${critical.length} critical error(s) require immediate attention`);
  if (remedyPct < 50) riskAreas.push(`Only ${remedyPct}% of observations have suggested remedies`);
  if (problems.length > 0) riskAreas.push(`${problems.length} problem(s) identified requiring follow-up`);

  const criticalFindings: CriticalFinding[] = [
    ...critical.map(c => ({
      title: c.title,
      description: c.description || 'No description provided',
      severity: 'critical' as const,
    })),
    ...problems.slice(0, 3).map(p => ({
      title: p.title,
      description: p.description || 'No description provided',
      severity: 'high' as const,
    })),
  ];

  // Group by category as themes
  const cats: Record<string, Observation[]> = {};
  obs.forEach(o => {
    if (!cats[o.category]) cats[o.category] = [];
    cats[o.category].push(o);
  });
  const themes: Theme[] = Object.entries(cats).map(([cat, catObs]) => ({
    theme: cat.charAt(0).toUpperCase() + cat.slice(1),
    description: `${catObs.length} observation(s) related to ${cat}`,
    observationCount: catObs.length,
    recommendations: catObs.filter(o => o.suggestedRemedy).map(o => o.suggestedRemedy).slice(0, 2),
  })).filter(t => t.observationCount > 0).sort((a, b) => b.observationCount - a.observationCount);

  const prioritizedRecommendations: PrioritizedRec[] = [];
  if (critical.length > 0) prioritizedRecommendations.push({
    priority: 1, action: 'Address critical errors immediately',
    rationale: `${critical.length} critical issue(s) require immediate resolution to prevent safety risks.`,
    timeframe: 'immediate',
  });
  if (remedyPct < 50) prioritizedRecommendations.push({
    priority: prioritizedRecommendations.length + 1, action: 'Improve remedy documentation',
    rationale: `Only ${remedyPct}% of observations have suggested remedies — this limits actionability.`,
    timeframe: 'short-term',
  });
  if (improvements.length > 0) prioritizedRecommendations.push({
    priority: prioritizedRecommendations.length + 1, action: 'Review improvement areas',
    rationale: `${improvements.length} area(s) flagged for improvement should be addressed before next operation.`,
    timeframe: 'short-term',
  });
  prioritizedRecommendations.push({
    priority: prioritizedRecommendations.length + 1, action: 'Conduct follow-up review',
    rationale: 'Schedule a follow-up session to verify corrective actions have been implemented.',
    timeframe: 'long-term',
  });

  const summary = `This ${incident.incidentType || 'emergency'} incident has ${total} observations recorded. ` +
    (critical.length > 0 ? `Critical issues represent ${Math.round((critical.length / total) * 100)}% of findings. ` : '') +
    `Remedy coverage is at ${remedyPct}%. ` +
    (label === 'Strong' ? 'Overall response performance is solid.' : label === 'Adequate' ? 'Performance is acceptable with room for improvement.' : 'Significant areas require attention.');

  return {
    executiveSummary: summary,
    keyStrengths,
    criticalFindings,
    themes,
    prioritizedRecommendations,
    riskAreas,
    overallAssessment: { score, label, summary },
    source: 'heuristic',
  };
}

export default function AIAnalysis({ incident, observations }: Props) {
  const [result, setResult] = useState<AIResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAnalysis = async (useAI: boolean) => {
    if (!useAI || observations.length === 0) {
      setResult(generateHeuristicAnalysis(incident, observations));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Format observations exactly like original backend
      const obsText = observations.map((o, i) =>
        `${i + 1}. [${o.tier.toUpperCase()}] "${o.title}"\n` +
        `   Category: ${o.category} | Phase: ${o.phase}\n` +
        `   Description: ${o.description}\n` +
        (o.suggestedRemedy ? `   Suggested Remedy: ${o.suggestedRemedy}\n` : '') +
        `   Submitted by: ${o.submittedBy}`
      ).join('\n\n');

      const prompt = `You are an expert emergency management analyst reviewing after-action observations.

INCIDENT: ${incident.name}
Type: ${incident.incidentType || 'Unknown'}
Location: ${incident.location || 'Unknown'}

OBSERVATIONS (${observations.length} total):
${obsText}

Analyze these observations thoroughly and provide a comprehensive after-action assessment. Respond ONLY with a valid JSON object (no markdown, no backticks):
{
  "executiveSummary": "2-4 paragraph narrative summary of the overall response, key findings, and state of operations",
  "keyStrengths": ["strength identified from the observations", "..."],
  "criticalFindings": [
    {"title": "finding title", "description": "detailed description", "severity": "critical|high|medium|low"}
  ],
  "themes": [
    {"theme": "recurring theme name", "description": "description of theme", "observationCount": 0, "recommendations": ["specific recommendation"]}
  ],
  "prioritizedRecommendations": [
    {"priority": 1, "action": "specific action to take", "rationale": "why this matters", "timeframe": "immediate|short-term|long-term"}
  ],
  "riskAreas": ["risk area description", "..."],
  "overallAssessment": {
    "score": 7,
    "label": "e.g. Good, Strong, Adequate, Needs Improvement, Critical",
    "summary": "one-sentence overall assessment"
  }
}`;

      const raw = await callClaude(
        [{ role: 'user', content: prompt }],
        {
          system: 'You are an expert emergency management analyst. Provide thorough, actionable analysis. Return only valid JSON, no markdown formatting.',
          maxTokens: 4096,
        }
      );

      // Strip markdown code fences if present
      let cleaned = raw.trim();
      if (cleaned.startsWith('```json')) cleaned = cleaned.slice(7);
      else if (cleaned.startsWith('```')) cleaned = cleaned.slice(3);
      if (cleaned.endsWith('```')) cleaned = cleaned.slice(0, -3);
      cleaned = cleaned.trim();

      const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Invalid response format');
      const parsed = JSON.parse(jsonMatch[0]) as AIResult;
      parsed.source = 'claude';
      setResult(parsed);
    } catch (err) {
      console.error('Claude analysis failed, using heuristic:', err);
      setResult(generateHeuristicAnalysis(incident, observations));
      setError('Claude analysis unavailable — showing heuristic analysis.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate heuristic on mount
  useEffect(() => {
    if (!result && !loading && observations.length > 0) {
      setResult(generateHeuristicAnalysis(incident, observations));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (observations.length === 0) {
    return (
      <Paper p="xl" radius="sm" withBorder>
        <Center><Text c="dimmed">No observations to analyze. Submit observations first.</Text></Center>
      </Paper>
    );
  }

  const scoreColor = result
    ? result.overallAssessment.score >= 8 ? 'var(--color-success)'
      : result.overallAssessment.score >= 6 ? 'var(--color-info)'
      : result.overallAssessment.score >= 4 ? 'var(--color-warning)'
      : semantic.error
    : secondary[700];

  const sevColor = (s: string) =>
    s === 'critical' ? 'var(--color-error)' : s === 'high' ? 'var(--color-error)' : s === 'medium' ? 'var(--color-warning)' : secondary[700];

  const copyReport = () => {
    if (!result) return;
    const text = [
      'HOT WASH — AI ANALYSIS REPORT',
      '================================',
      `Incident: ${incident.name}`,
      `Type: ${incident.incidentType || 'N/A'} | Status: ${incident.status} | Location: ${incident.location || 'N/A'}`,
      `Source: ${result.source === 'claude' ? 'Claude AI Analysis' : 'Heuristic Analysis'}`,
      `Score: ${result.overallAssessment.score}/10 (${result.overallAssessment.label})`,
      '',
      'EXECUTIVE SUMMARY',
      '─────────────────',
      result.executiveSummary,
      '',
      'KEY STRENGTHS',
      '─────────────',
      ...result.keyStrengths.map(s => `  + ${s}`),
      '',
      'RISK AREAS',
      '──────────',
      ...result.riskAreas.map(r => `  ! ${r}`),
      '',
      'CRITICAL FINDINGS',
      '─────────────────',
      ...result.criticalFindings.map(f => `  [${f.severity.toUpperCase()}] ${f.title}\n    ${f.description}`),
      '',
      'THEMES',
      '──────',
      ...result.themes.map(t => `  ${t.theme} (${t.observationCount} observations)\n    ${t.description}\n    Recs: ${t.recommendations.join('; ')}`),
      '',
      'PRIORITIZED RECOMMENDATIONS',
      '───────────────────────────',
      ...result.prioritizedRecommendations.map(r => `  ${r.priority}. [${r.timeframe}] ${r.action}\n     ${r.rationale}`),
    ].join('\n');
    navigator.clipboard.writeText(text);
  };

  return (
    <Stack gap="md">
      {/* Controls */}
      <Group justify="space-between">
        <Group gap="xs">
          <Button
            leftSection={<IconSparkles size={14} />}
            onClick={() => runAnalysis(true)}
            loading={loading}
            size="xs"
          >
            {result?.source === 'claude' ? 'Regenerate Analysis' : 'Run Claude Analysis'}
          </Button>
          {result && (
            <Badge size="sm" variant="light" color={result.source === 'claude' ? 'blurple' : 'gray'}>
              {result.source === 'claude' ? 'Powered by Claude' : 'Heuristic'}
            </Badge>
          )}
        </Group>
        {result && (
          <Button variant="subtle" size="xs" leftSection={<IconCopy size={14} />} onClick={copyReport}>
            Copy Report
          </Button>
        )}
      </Group>

      {error && (
        <Paper p="xs" radius="sm" bg="var(--warning-light)" style={{ border: '1px solid var(--color-warning)' }}>
          <Text size="xs" c="var(--warning)">{error}</Text>
        </Paper>
      )}

      {loading && (
        <Paper p="xl" radius="sm" withBorder>
          <Center>
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Claude is analyzing {observations.length} observations...</Text>
              <Text size="xs" c="dimmed">This usually takes 15-30 seconds</Text>
            </Stack>
          </Center>
        </Paper>
      )}

      {result && !loading && (
        <>
          {/* Overall Assessment Header */}
          <Paper p="md" radius="sm" withBorder>
            <Group gap="lg">
              <div style={{
                width: 72, height: 72, borderRadius: 4,
                background: scoreColor + '15', border: `2px solid ${scoreColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column',
              }}>
                <Text fw={700} fz={28} style={{ color: scoreColor, lineHeight: 1 }}>{result.overallAssessment.score}</Text>
                <Text fz={10} c="dimmed">/10</Text>
              </div>
              <div style={{ flex: 1 }}>
                <Group gap="xs" mb="xs">
                  <Badge size="sm" style={{ background: scoreColor + '15', color: scoreColor }}>
                    {result.overallAssessment.label}
                  </Badge>
                  <Text size="xs" c="dimmed">{observations.length} observations analyzed</Text>
                </Group>
                <Text size="sm">{result.overallAssessment.summary}</Text>
                <SimpleGrid cols={3} mt="xs">
                  <div>
                    <Text fz={10} c="dimmed">Critical</Text>
                    <Text fw={600} size="sm" c={semantic.error}>
                      {observations.filter(o => o.tier === 'critical_error').length}
                    </Text>
                  </div>
                  <div>
                    <Text fz={10} c="dimmed">Remedied</Text>
                    <Text fw={600} size="sm" c={semantic.success}>
                      {Math.round((observations.filter(o => o.suggestedRemedy).length / observations.length) * 100)}%
                    </Text>
                  </div>
                  <div>
                    <Text fz={10} c="dimmed">Contributors</Text>
                    <Text fw={600} size="sm">{new Set(observations.map(o => o.submittedBy)).size}</Text>
                  </div>
                </SimpleGrid>
              </div>
            </Group>
          </Paper>

          {/* Executive Summary */}
          <Paper p="md" radius="sm" withBorder>
            <Text fw={600} size="sm" mb="xs">Executive Summary</Text>
            <Text size="sm" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {result.executiveSummary}
            </Text>
          </Paper>

          {/* Key Strengths & Risk Areas */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="xs">Key Strengths</Text>
              <Stack gap="xs">
                {result.keyStrengths.map((s, i) => (
                  <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                    <ThemeIcon size="xs" color="green" variant="light" radius="xl" mt={3}>
                      <IconCheck size={10} />
                    </ThemeIcon>
                    <Text size="sm">{s}</Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="xs">Risk Areas</Text>
              {result.riskAreas.length === 0 ? (
                <Text size="sm" c="dimmed">No significant risks identified.</Text>
              ) : (
                <Stack gap="xs">
                  {result.riskAreas.map((r, i) => (
                    <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                      <ThemeIcon size="xs" color="red" variant="light" radius="xl" mt={3}>
                        <IconAlertTriangle size={10} />
                      </ThemeIcon>
                      <Text size="sm">{r}</Text>
                    </Group>
                  ))}
                </Stack>
              )}
            </Paper>
          </SimpleGrid>

          {/* Critical Findings */}
          {result.criticalFindings.length > 0 && (
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="sm">Critical Findings</Text>
              <Stack gap="sm">
                {result.criticalFindings.map((f, i) => (
                  <Paper key={i} p="sm" radius="sm" bg="var(--bg-secondary)" style={{ border: `1px solid ${sevColor(f.severity)}20` }}>
                    <Group gap="xs" mb={4}>
                      <ThemeIcon size="xs" variant="light" radius="xl"
                        color={f.severity === 'critical' ? 'red' : f.severity === 'high' ? 'orange' : f.severity === 'medium' ? 'yellow' : 'gray'}>
                        <IconAlertCircle size={10} />
                      </ThemeIcon>
                      <Text size="sm" fw={600}>{f.title}</Text>
                      <Badge size="xs" style={{ background: sevColor(f.severity) + '15', color: sevColor(f.severity) }}>
                        {f.severity}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" pl={24}>{f.description}</Text>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Themes */}
          {result.themes.length > 0 && (
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="sm">Identified Themes</Text>
              <Stack gap="sm">
                {result.themes.map((t, i) => (
                  <Paper key={i} p="sm" radius="sm" withBorder>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <ThemeIcon size="xs" color="blurple" variant="light" radius="xl">
                          <IconBulb size={10} />
                        </ThemeIcon>
                        <Text size="sm" fw={600}>{t.theme}</Text>
                      </Group>
                      <Badge size="xs" variant="light" color="gray">
                        {t.observationCount} observation{t.observationCount !== 1 ? 's' : ''}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" mb="xs" pl={24}>{t.description}</Text>
                    {t.recommendations.length > 0 && (
                      <Stack gap={4} pl={24}>
                        {t.recommendations.map((rec, j) => (
                          <Text key={j} size="xs" c="blurple">→ {rec}</Text>
                        ))}
                      </Stack>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Prioritized Recommendations */}
          <Paper p="md" radius="sm" withBorder>
            <Text fw={600} size="sm" mb="sm">Prioritized Recommendations</Text>
            <Stack gap="sm">
              {result.prioritizedRecommendations.map((r) => (
                <Group key={r.priority} gap="sm" wrap="nowrap" align="flex-start">
                  <Text fw={700} fz={18} c="blurple" style={{ minWidth: 28, textAlign: 'center', lineHeight: 1 }}>
                    {r.priority}
                  </Text>
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" mb={2}>
                      <Text size="sm" fw={600}>{r.action}</Text>
                      <Badge size="xs"
                        color={r.timeframe === 'immediate' ? 'red' : r.timeframe === 'short-term' ? 'yellow' : 'gray'}>
                        {r.timeframe === 'immediate' ? 'Immediate' : r.timeframe === 'short-term' ? 'Short-term' : 'Long-term'}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">{r.rationale}</Text>
                  </div>
                </Group>
              ))}
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  );
}
